// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image, { StaticImageData } from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img - reused from the AIR-1000 product page
import Office from 'public/img/products/Air1000/office.webp'
import Schools from 'public/img/products/Air1000/schools.webp'
import CarRepair from 'public/img/products/Air1000/car_repair_shop.jpg'
import IndustrialFacilities from 'public/img/products/Air1000/IndustrialFacilities.png'
import PaintShops from 'public/img/products/Air1000/PaintShops.png'
import WoodworkingShops from 'public/img/products/Air1000/woodworking_shop.jpg'
import IndustrialKitchens from 'public/img/products/Air1000/IndustrialKitchens.png'
import IndoorEvents from 'public/img/products/Air1000/indoor_event.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

type Application = {
  img: StaticImageData
  title: string
  description: string
}

export default function ApplicationsSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width

  const applications: Application[] = [
    {
      img: Office,
      title: t('applications.items.01.title'),
      description: t('applications.items.01.description')
    },
    {
      img: Schools,
      title: t('applications.items.02.title'),
      description: t('applications.items.02.description')
    },
    {
      img: CarRepair,
      title: t('applications.items.03.title'),
      description: t('applications.items.03.description')
    },
    {
      img: IndustrialFacilities,
      title: t('applications.items.04.title'),
      description: t('applications.items.04.description')
    },
    {
      img: PaintShops,
      title: t('applications.items.05.title'),
      description: t('applications.items.05.description')
    },
    {
      img: WoodworkingShops,
      title: t('applications.items.06.title'),
      description: t('applications.items.06.description')
    },
    {
      img: IndustrialKitchens,
      title: t('applications.items.07.title'),
      description: t('applications.items.07.description')
    },
    {
      img: IndoorEvents,
      title: t('applications.items.08.title'),
      description: t('applications.items.08.description')
    }
  ]

  const getGrid = () => {
    if (w > breakpoints.desktopMid) return 'repeat(4, 1fr)'
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    if (w > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getCard = (application: Application) => (
    <Box
      key={application.title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 10' }}>
        <Image
          src={application.img}
          // The card title names what the photo shows, so it doubles as alt.
          alt={application.title}
          fill
          sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 25vw"
          style={{ objectFit: 'cover' }}
        />
      </Box>

      <Box sx={{ padding: '1.25rem' }}>
        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: 1.3,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.6rem'
          }}
        >
          {application.title}
        </Typography>

        <Typography
          component="p"
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.6,
            color: themeColors.gray.defaultText
          }}
        >
          {application.description}
        </Typography>
      </Box>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('applications.eyebrow')}
        title={t('applications.title')}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {applications.map(application => getCard(application))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
